import { useRef } from 'react'

import { useTruncateText } from 'src/shared/hooks/use-truncate-text'
import { cn } from 'src/shared/utils/cn'

interface TruncatedTextProps {
  text: string
  numberOfLines: number
  /**
   * A custom component to display when the text is truncated.
   */
  customOverflowComponent?: React.ReactNode
  /**
   * If true, the text will be truncated to the nearest whole word. Otherwise, it will be truncated to the nearest character.
   */
  shouldTruncateWholeWords?: boolean
  className?: string

  /** Force the text container to shrink when the screen is resized. */
  forceShrink?: boolean
}

/**
 * This component is used to truncate text and display a custom overflow component when the text is truncated.
 *
 * Note: When using this component in a flex container, make sure to add a min-w-0 class to the parent container (for proper resizing).
 */
export default function TruncatedText({
  text,
  customOverflowComponent,
  numberOfLines = 1,
  shouldTruncateWholeWords = true,
  className,
  forceShrink = false,
}: TruncatedTextProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const overflowElementRef = useRef<HTMLDivElement>(null)

  const { truncatedText, truncatedTextArray } = useTruncateText({
    text,
    numberOfLines,
    shouldTruncateWholeWords,
    containerElement: containerRef,
    overflowElement: overflowElementRef,
  })

  const didTruncate = truncatedText.length !== text.length

  // Workaround for the issue where the container that contains this component is not shrinking when screen size is changed.
  if (forceShrink) {
    return (
      <div className="relative w-full">
        <div ref={containerRef} className={cn('absolute inset-0 w-full overflow-visible text-nowrap', className)}>
          {truncatedTextArray.map((textRow, index) => {
            if (index === truncatedTextArray.length - 1) {
              return null
            }
            return (
              <p key={index} className="block w-full text-nowrap">
                {textRow}
              </p>
            )
          })}
          <div className="block w-full text-nowrap">
            {truncatedTextArray[truncatedTextArray.length - 1]}
            <span ref={overflowElementRef} className="inline-block">
              {didTruncate && customOverflowComponent !== undefined && customOverflowComponent}
            </span>
          </div>
        </div>
        <div
          className="pointer-events-none text-transparent"
          style={{
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: numberOfLines,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {text}
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className={cn('w-full overflow-visible text-nowrap', className)}>
      {truncatedTextArray.map((textRow, index) => {
        if (index === truncatedTextArray.length - 1) {
          return null
        }
        return (
          <p key={index} className="block w-full text-nowrap text-muted">
            {textRow}
          </p>
        )
      })}
      <div className="block w-full text-nowrap text-muted">
        {truncatedTextArray[truncatedTextArray.length - 1]}
        <span ref={overflowElementRef} className="inline-block">
          {didTruncate && customOverflowComponent !== undefined && customOverflowComponent}
        </span>
      </div>
    </div>
  )
}
